import axios from 'axios';
import { inventoryAPI, type InventoryItem, type InventoryTransaction, type CreateInventoryItem, type UpdateInventoryItem, type StockUpdate, type GetItemsParams, type GetTransactionsParams, type InventorySummary } from './inventoryAPI';
import { financialReportsAPI, type ProfitLossStatement, type BalanceSheet, type CashFlowStatement, type TrialBalance, type GeneralLedger, type ReportParams } from './financialReportsAPI';
import { accountingAPI, type FinancialSummary, type CashFlowSummary, type ManualJournalEntry, type TrialBalanceValidation } from './accountingAPI';
import { modulesAPI, type Module, type ModuleStatusResponse, type AvailableModulesResponse, type EnableModuleResponse, type ModuleSettings } from './modulesAPI';
import { sitePricingAPI, type SitePricing, type SitePricingResponse } from './sitePricingAPI';
import { invoicesAPI, type Invoice, type InvoicesResponse, type InvoiceStatistics } from './invoicesAPI';
import { subscriptionAPI, type Subscription, type SubscriptionStatus, type PaymentMethod } from './subscriptionAPI';
import { unifiedStorage } from './unifiedStorage';
import { isOfflineMode } from '../config/appConfig';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Services API
export const servicesAPI = {
  getCategories: async () => {
    if (isOfflineMode()) {
      // Return empty categories in offline mode (could be cached)
      return { data: [] };
    }
    return api.get('/pos/categories');
  },
  updateCategories: (categories: any[]) => api.put('/pos/categories', { categories }),
  getServices: async () => {
    if (isOfflineMode()) {
      // Use local storage in offline mode
      const services = await unifiedStorage.getServices();
      return { data: services };
    }
    const response = await api.get('/pos/services');
    // Cache services for offline use
    if (response.data) {
      await unifiedStorage.saveServices(Array.isArray(response.data) ? response.data : response.data.data || []);
    }
    return response;
  },
  createService: (data: any) => api.post('/pos/services', data),
  updateService: (id: string, data: any) => api.put(`/pos/services/${id}`, data),
  deleteService: (id: string) => api.delete(`/pos/services/${id}`),
  copyServicesFromSite: (sourceSiteId: string) => api.post('/pos/services/copy', { sourceSiteId }),
};

// Customers API
export const customersAPI = {
  getCustomers: async (params?: any) => {
    if (isOfflineMode()) {
      const customers = await unifiedStorage.getCustomers();
      return { data: customers };
    }
    if (params && Object.keys(params).length > 0) {
      return api.get('/customers', { params });
    }
    return api.get('/customers');
  },
  getCustomer: async (id: string) => {
    if (isOfflineMode()) {
      const customer = await unifiedStorage.getCustomer(id);
      return { data: customer };
    }
    return api.get(`/customers/${id}`);
  },
  createCustomer: async (data: any) => {
    if (isOfflineMode()) {
      await unifiedStorage.saveCustomer(data);
      return { data };
    }
    const response = await api.post('/customers', data);
    // Cache for offline
    if (response.data) {
      await unifiedStorage.saveCustomer(response.data);
    }
    return response;
  },
  updateCustomer: (id: string, data: any) => api.put(`/customers/${id}`, data),
  deleteCustomer: (id: string) => api.delete(`/customers/${id}`),
  addCustomerNote: (id: string, note: string) => api.post(`/customers/${id}/notes`, { note }),
};

// Orders API
export const ordersAPI = {
  getOrders: async (params?: any) => {
    if (isOfflineMode()) {
      const orders = await unifiedStorage.getOrders();
      return { data: orders };
    }
    if (params && Object.keys(params).length > 0) {
      return api.get('/orders', { params });
    }
    return api.get('/orders');
  },
  getOrder: async (id: string) => {
    if (isOfflineMode()) {
      const order = await unifiedStorage.getOrder(id);
      return { data: order };
    }
    return api.get(`/orders/${id}`);
  },
  createOrder: async (data: any) => {
    if (isOfflineMode()) {
      await unifiedStorage.saveOrder(data);
      return { data };
    }
    const response = await api.post('/orders', data);
    // Cache for offline
    if (response.data) {
      await unifiedStorage.saveOrder(response.data);
    }
    return response;
  },
  updateOrder: (id: string, data: any) => api.put(`/orders/${id}`, data),
  updateOrderStatus: (id: string, status: string) => api.put(`/orders/${id}/status`, { status }),
  updateOrderItemStatus: (orderId: string, itemId: string, status: string) => api.put(`/orders/${orderId}/items/${itemId}/status`, { status }),
  getPendingJobs: () => api.get('/orders/jobs/pending'),
  updateOrderDueDate: (id: string, dueDate: string) => api.put(`/orders/${id}`, { dueDate }),
  deleteOrder: (id: string) => api.delete(`/orders/${id}`),
  lookupByOrderNumber: (orderNumber: string) => api.get(`/orders/lookup/${orderNumber}`),
};

// Notifications API
export const notificationsAPI = {
  getConfig: () => api.get('/notifications/config'),
  sendOrderNotification: (orderId: string, data: { channels: string[]; customMessage?: string }) =>
    api.post(`/notifications/order/${orderId}/send`, data),
  getOrderHistory: (orderId: string) => api.get(`/notifications/order/${orderId}/history`),
  emailReceipt: (orderId: string, email?: string) => api.post(`/notifications/order/${orderId}/receipt`, { email }),
};

// POS API
export const posAPI = {
  createOrder: (data: any) => api.post('/pos/orders', data),
  processPayment: (data: any) => api.post('/pos/payments', data),
  getReceipt: (orderId: string) => api.get(`/pos/receipt/${orderId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentOrders: () => api.get('/dashboard/recent-orders'),
  getCustomerStats: () => api.get('/dashboard/customer-stats'),
  getRevenueReport: () => api.get('/dashboard/revenue-report'),
  getPopularServices: () => api.get('/dashboard/popular-services'),
  getRevenueChartData: () => api.get('/dashboard/revenue-chart'),
};

// Sites API
export const sitesAPI = {
  getSites: () => api.get('/sites'),
  getSite: (id: string) => api.get(`/sites/${id}`),
  createSite: (data: any) => api.post('/sites', data),
  updateSite: (id: string, data: any) => api.put(`/sites/${id}`, data),
  deleteSite: (id: string) => api.delete(`/sites/${id}`),
  assignSiteToUser: (userId: string, siteId: string) => api.post(`/sites/users/${userId}/assign`, { siteId }),
};

// Users API
export const usersAPI = {
  getUsers: (params?: any) => {
    if (params && Object.keys(params).length > 0) {
      return api.get('/users', { params });
    }
    return api.get('/users');
  },
  getUser: (id: string) => api.get(`/users/${id}`),
  createUser: (data: any) => api.post('/users/register', data),
  updateUser: (id: string, data: any) => api.put(`/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/users/${id}`),
};

// Payments API
export const paymentsAPI = {
  getConfig: () => api.get('/payments/config'),
  createIntent: (orderId: string, amount?: number) => api.post('/payments/intent', { orderId, amount }),
  createTerminalToken: () => api.post('/payments/terminal/token'),
  createTerminalIntent: (orderId: string, amount?: number) => api.post('/payments/terminal/intent', { orderId, amount }),
  captureTerminalPayment: (paymentIntentId: string) => api.post('/payments/terminal/capture', { paymentIntentId }),
};

// Expenses API
export const expensesAPI = {
  getExpenses: (params?: any) => api.get('/expenses', { params }),
  getExpense: (id: string) => api.get(`/expenses/${id}`),
  createExpense: (data: FormData) => api.post('/expenses', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateExpense: (id: string, data: FormData) => api.put(`/expenses/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteExpense: (id: string) => api.delete(`/expenses/${id}`),
  getSummary: (params?: any) => api.get('/expenses/summary', { params })
};

// Loyalty API
export const loyaltyAPI = {
  getSettings: () => api.get('/loyalty/settings'),
  updateSettings: (data: any) => api.put('/loyalty/settings', data),
  getCustomerLoyalty: (customerId: string) => api.get(`/loyalty/customer/${customerId}`),
  earnPoints: (data: { customerId: string; orderId: string; amount: number }) => api.post('/loyalty/earn', data),
  redeemPoints: (data: { customerId: string; orderId: string; points: number }) => api.post('/loyalty/redeem', data),
  adjustPoints: (data: { customerId: string; points: number; description?: string }) => api.post('/loyalty/adjust', data),
  getTransactions: (params?: any) => api.get('/loyalty/transactions', { params }),
};

// Discounts API
export const discountsAPI = {
  getDiscounts: () => api.get('/discounts'),
  createDiscount: (data: any) => api.post('/discounts', data),
  updateDiscount: (id: string, data: any) => api.put(`/discounts/${id}`, data),
  deleteDiscount: (id: string) => api.delete(`/discounts/${id}`),
  validateDiscount: (data: { code: string; subtotal: number; customerId?: string }) => api.post('/discounts/validate', data),
  applyDiscount: (data: { discountId: string; orderId: string; discountAmount: number }) => api.post('/discounts/apply', data),
};

// Reports API
export const reportsAPI = {
  getSalesSummary: (params: { startDate: string; endDate: string }) => api.get('/reports/sales', { params }),
  getServicePerformance: (params: { startDate: string; endDate: string }) => api.get('/reports/services', { params }),
  getPaymentReport: (params: { startDate: string; endDate: string }) => api.get('/reports/payments', { params }),
  getCustomerReport: (params: { startDate: string; endDate: string }) => api.get('/reports/customers', { params }),
  getInventoryReport: (params: { startDate: string; endDate: string }) => api.get('/reports/inventory', { params }),
  getExpenseReport: (params: { startDate: string; endDate: string }) => api.get('/reports/expenses', { params }),
};

// Export inventory API
export { inventoryAPI };
export type { InventoryItem, InventoryTransaction, CreateInventoryItem, UpdateInventoryItem, StockUpdate, GetItemsParams, GetTransactionsParams, InventorySummary };

// Export financial reports API
export { financialReportsAPI };
export type { ProfitLossStatement, BalanceSheet, CashFlowStatement, TrialBalance, GeneralLedger, ReportParams };

// Export accounting API
export { accountingAPI };
export type { FinancialSummary, CashFlowSummary, ManualJournalEntry, TrialBalanceValidation };

// Export modules API
export { modulesAPI };
export type { Module, ModuleStatusResponse, AvailableModulesResponse, EnableModuleResponse, ModuleSettings };

export default api;
